import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TiCampaignBuilder, blankTcDraft, saveTcDraft, tcCampaignById, useToast } from '@elva/ui';
import type { TcBuilderPage, TcCampaignDraft } from '@elva/ui';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';

/**
 * The TC campaign builder (amendment 27 §5). Three pages over one draft, held in
 * a module-level store so the draft survives the page-to-page navigation — the
 * same pattern the recall builder uses.
 *
 * `/campaigns/new` (optionally `?desc=` from Opportunity's *Start a campaign*)
 * and `/campaigns/<id>/edit`.
 */
const store: { draft: TcCampaignDraft; loadedFor: string | undefined } = {
  draft: blankTcDraft(),
  loadedFor: undefined,
};

function loadFor(key: string, desc: string): TcCampaignDraft {
  if (store.loadedFor === key) return store.draft;
  if (key === 'new') {
    store.draft = blankTcDraft(desc);
  } else {
    const row = tcCampaignById(key);
    store.draft = { ...blankTcDraft(row?.description ?? ''), id: key, name: row?.name ?? '' };
  }
  store.loadedFor = key;
  return store.draft;
}

function useTcBuilder(key: string, desc: string) {
  const [draft, setLocal] = useState<TcCampaignDraft>(() => loadFor(key, desc));
  const update = useCallback((next: TcCampaignDraft) => { store.draft = next; setLocal(next); }, []);
  return [draft, update] as const;
}

export function TiCampaignBuilderPage({ page }: { page: TcBuilderPage }) {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const key = id ?? 'new';
  const { toast } = useToast();
  const [draft, setDraft] = useTcBuilder(key, params.get('desc') ?? '');

  if (id !== undefined && tcCampaignById(id) === undefined) return <Navigate to="/treatment-intelligence/campaigns" replace />;

  const base = id === undefined ? '/treatment-intelligence/campaigns/new' : `/treatment-intelligence/campaigns/${id}/edit`;

  return (
    <TiCampaignBuilder
      page={page}
      draft={draft}
      onChange={setDraft}
      onGoTo={(p) => { void navigate(`${base}${p === 'audience' ? '' : `/${p}`}`); }}
      onCancel={() => { void navigate('/treatment-intelligence/campaigns'); }}
      onSaveDraft={() => { saveTcDraft(draft); void navigate('/treatment-intelligence/campaigns'); }}
      onStart={() => {
        saveTcDraft(draft);
        // Start toast (fix 29 §4.3), e.g. "Freshly diagnosed started — 61 patients".
        toast({ title: `${draft.name || 'Campaign'} started — 61 patients`, variant: 'success' });
        void navigate('/treatment-intelligence/campaigns');
      }}
    />
  );
}
